import { ExecutableLessonFile } from '@customtypes/parsedLesson';
import { postUserReport, UserReportOption } from '@requests/userReportsRequest';
import { User } from 'next-firebase-auth';
import { assign, createMachine, fromPromise } from 'xstate';

type ReportModalContextType = {
  reason: UserReportOption;
  message: string;
  user: User;
  tutorialId: number;
  tutorialVersion: number;
  interactionType: string;
  lessonId: number;
};

const submitUserReport = fromPromise(
  async ({
    input,
  }: {
    input: {
      user: User;
      tutorialId: number;
      lessonId: number;
      report: {
        reportOption: UserReportOption;
        reportText: string;
        tutorialVersion: number;
        interactionType: string;
        userCode?: ExecutableLessonFile[];
      };
    };
  }) => {
    return postUserReport(input);
  },
);

export const reportModalMachine = createMachine(
  {
    /** @xstate-layout N4IgpgJg5mDOIC5QFVZgE4AIBKYAOA9ugC6YCyAhgMYAWAlgHZgB0AwjQQWjmBbAQwDEaUozrE6FADaYqA4mAAexANoAGALqJQhWOLoDtIRYgAcAJgCszNQGYAnGtMB2AIwXnlzwBoQAT0RzZwAWZmC1ADZzU1tnOzVXL1sAX2TfVAweQhJyanomNg4uMB4+AWEwKTAqUnRefgZ1LSQQXX1DFpMEGNNmKNcB82DY12DnCN8AhFdnZ2Z7S1tRiPtY+3tzCODU9LQsXGzSSlpGFnZOblwyoTkGCQYAVxKAd3EaTDrrpqM2iQ7QLquNRqOYWUzLCIhNTmDaTRC2GJhYJBUxqDbQ+xRFJpEAZfb4IhHPKnQoXEpXBqCOoib4tX4GBhGLqmCK2ZieYHmWyWYKmSzuSxw7quZiWYEJBLOGErSw7XF7LKE3InAoAQSoVHw9yg5DgsAoMEwAEkGHgHsRBIwzaQALZ6g1gWk6LjtRmdRCjeyhXnBcKmcEJWy2IXRUEOVnAyzooIROV4xU5Y75FjqzV4bW62D6w0m63CB4AIxt4g+BJITtaLr+boBHtGamYpnspl9LISlnMnaF-PsNjbIJiCXsrjjCoOSqTJNTWsYOrI9pzpvNVLgYFUmh+VYZTI9tmCEXm5lcjl5zm5vvMIZZ8ybW2CiQ7Z5HOPj48TxIKAGVC8XiBm3xaEACCwjAAG4EAA1iwsA-uIAEVvS-zGPC-o2HEthcqy9hniCQq2GooT+tErL8hEEQESEo6ZAByrJsw35FuI-5lhaGDoEQzB4FIFDEAAZkQNrMDBjHEPBG50luSFdAivQgnYmEODhzghkGzCuBhLjiusw62LGL5jixtEkgxv7MYc+YiQoEAIZJNbIQgMlofJumKfhyn+B6Fh9BpUbqdycQLFR+KHEZX6wX+s4Jha1JrjZejVjuDmoXJGEudhbl4R2zAIqGERikGzimEFUWhSwNFoHcK40uJzrxdu7oIG4vaQsewLIvhILuVM5hOH0lgRuCPUslsqQ4gwBAQHARivoZk5MJudVSYgAC0EweQgq02OK207dExU0XNZxFJc9RIYhdnMhhjaOFseUYdCsR4Z4jZRsiBERKiCL2Pts0fimGozgwc4LiUubmgtrqJaMXjMNEjjDqyGFenhTawxs4wfUMnZRj9IWHfR4VmYSEMJQ1yJCvuvZWJsZHOCesyeLjE5-cw5VgHcJP1bWCCWKiL2rM2UZBv6wbrVy5hqRpjgEbyTidqNyRAA */
    id: 'User Report Machine',

    context: {
      reason: 'content_typo',
      message: '',
    } as ReportModalContextType,
    initial: 'Choose Reason',
    states: {
      'Choose Reason': {
        on: {
          'set initial context': {
            target: 'Choose Reason',
            actions: 'setInitialContext',
          },
          'select reason': {
            target: 'Choose Reason',
            actions: 'selectReason',
          },
          'continue with reason': {
            target: 'Accepting Message Input',
            guard: 'validateIfReasonHasBeenSelected',
          },
          reset: {
            target: 'Choose Reason',
            actions: 'reset',
          },
        },
      },
      'Accepting Message Input': {
        on: {
          'input message': {
            target: 'Accepting Message Input',
            actions: 'inputMessage',
          },
          'submit report': {
            target: 'Submitting Report',
            guard: 'isMessageNotEmpty',
          },
          reset: {
            target: 'Choose Reason',
            actions: 'reset',
          },
        },
      },
      'Submitting Report': {
        on: {
          submitted: {
            target: 'Report sent',
          },
          reset: {
            target: 'Choose Reason',
            actions: 'reset',
          },
        },
        invoke: {
          id: 'submitReport',
          src: 'submitReport',
          input: ({ context, event }) => {
            return {
              user: context.user,
              tutorialId: context.tutorialId,
              lessonId: context.lessonId,
              report: {
                reportOption: context.reason,
                reportText: context.message,
                tutorialVersion: context.tutorialVersion,
                interactionType: context.interactionType,
                userCode:
                  event.type === 'submit report' ? event.userCode : undefined,
              },
            };
          },
          onDone: {
            target: 'Report sent',
          },
          onError: {
            target: 'Report sent',
          },
        },
      },
      'Report sent': {
        on: {
          reset: {
            target: 'Choose Reason',
            actions: 'reset',
          },
        },
      },
    },
    types: {} as {
      context: ReportModalContextType;
      actors: {
        src: 'submitReport'; // src name (inline behaviors ideally inferred)
        id: 'submitReport'; // possible ids (optional)
        logic: typeof submitUserReport; // actor logic (inline behaviors)
      };
      events:
        | {
            type: 'set initial context';
            user: User;
            tutorialId: number;
            tutorialVersion: number;
            lessonId: number;
          }
        | {
            type: 'select reason';
            reason: UserReportOption;
          }
        | {
            type: 'input message';
            message: string;
          }
        | {
            type: 'submit report';
            userCode?: ExecutableLessonFile[];
          }
        | {
            type: 'submitted';
          }
        | {
            type: 'reset';
          }
        | {
            type: 'continue with reason';
          };
    },
  },
  {
    actions: {
      setInitialContext: assign(({ context, event }) => {
        return { ...context, ...event };
      }),
      selectReason: assign(({ context, event }) => {
        if (event.type === 'select reason') {
          return { ...context, reason: event.reason };
        }
        return context;
      }),
      inputMessage: assign(({ context, event }) => {
        if (event.type === 'input message') {
          return { ...context, message: event.message };
        }
        return context;
      }),
      reset: assign(({ context, event }) => {
        return {
          reason: 'content_typo',
          message: '',
        } as ReportModalContextType;
      }),
    },
    actors: {
      submitReport: submitUserReport,
    },
    guards: {
      validateIfReasonHasBeenSelected: ({ context, event }) => {
        return context.reason !== null;
      },
      isMessageNotEmpty: ({ context, event }) => {
        return context.message !== '';
      },
    },
    delays: {},
  },
);
